/**
 * Unified Add-to-Cart System - FIXED Completion Behavior
 * Fixed: Project clearing after add-to-cart when returning to saved projects
 */
(() => {
  'use strict';

  // System state and configuration
  let initialized = false;
  let addToCartForm = null;
  let addToCartButton = null;
  let celebrationHeader = null;
  let originalButtonText = '';
  let currentState = 'initializing';
  let stepNumber = 2;
  let validationCache = new Map();
  let performanceMetrics = {
    stateChanges: 0,
    headerUpdates: 0,
    buttonInteractions: 0,
    submissionTimes: [],
    errorCounts: 0
  };

  const stateManager = window.PitchPrintStateManager;
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[UnifiedAddToCart]', ...args);

  if (!stateManager) {
    console.error('[UnifiedAddToCart] StateManager not found - unified system disabled');
    return;
  }

  // =====================================
  // FIXED: UTILITY FUNCTIONS DECLARED FIRST
  // =====================================

  const isPitchPrintProduct = () => {
    // First, check window.productConfig (most reliable - uses metafield values)
    if (window.productConfig && window.productConfig.usesPitchprint !== undefined) {
      const usesPitchprint = window.productConfig.usesPitchprint;
      log('🔍 Window productConfig.usesPitchprint:', usesPitchprint);
      return usesPitchprint === true;
    }

    // Second, check the per-product metafield exposed as a form data attribute.
    // Keeps us in sync with the Enhanced integration on templates that don't
    // render buy-buttons.liquid (e.g. the buy_buttons block is disabled), where
    // window.productConfig is never set.
    const ppForm = document.querySelector('form[action*="/cart/add"]');
    if (ppForm && ppForm.dataset.usesPitchprint !== undefined) {
      const usesPitchprint = ppForm.dataset.usesPitchprint;
      log('🔍 Data attribute uses_pitchprint:', usesPitchprint);
      if (usesPitchprint === 'false' || usesPitchprint === '') return false;
      if (usesPitchprint === 'true') return true;
    }

    // Last resort: DOM detection. IMPORTANT — only use PRODUCT-SCOPED signals.
    // Do NOT test for global PitchPrint signals (site-wide <script src*="pitchprint">
    // tags or window.ppclient) — those exist on EVERY page, so they misclassify
    // any non-PitchPrint product (bundles, standard items) as PitchPrint, stranding
    // it on the "Ready to Design?" state and blocking add-to-cart.
    // NOTE: .custom-tab-body is the variant-selector container (card-variants.liquid)
    // and renders for ANY product with variants — it is NOT PitchPrint-specific, so it
    // must not be used as an indicator. #pp_main_btn_sec and #pitchprint/#_pitchprint
    // only render when uses_pitchprint is true.
    log('⚠️ Metafield not found in window.productConfig or data attribute, falling back to scoped DOM detection');
    const indicators = [
      () => document.querySelector('#pitchprint, #_pitchprint'),
      () => document.querySelector('#pp_main_btn_sec')
    ];

    return indicators.some(check => {
      try {
        return check();
      } catch (e) {
        return false;
      }
    });
  };

  const isFileUploadProduct = () => {
    // First, check window.productConfig (most reliable - uses metafield values)
    if (window.productConfig && window.productConfig.enableFileUpload !== undefined) {
      const enableFileUpload = window.productConfig.enableFileUpload;
      log('🔍 Window productConfig.enableFileUpload:', enableFileUpload);
      return enableFileUpload === true;
    }

    // Second, check the per-product metafield exposed as a form data attribute
    // (keeps us in sync with the Enhanced integration when window.productConfig
    // is not set, e.g. templates with the buy_buttons block disabled).
    const uploadForm = document.querySelector('form[action*="/cart/add"]');
    if (uploadForm && uploadForm.dataset.enableFileUpload !== undefined) {
      const enableFileUpload = uploadForm.dataset.enableFileUpload;
      log('🔍 Data attribute enable_file_upload:', enableFileUpload);
      if (enableFileUpload === 'false' || enableFileUpload === '') return false;
      if (enableFileUpload === 'true') return true;
    }

    // Fallback to DOM detection for backwards compatibility
    log('⚠️ File upload metafield not found in window.productConfig or data attribute, falling back to DOM detection');
    const indicators = [
      () => document.querySelector('#file-upload-container'),
      () => document.querySelector('.file-upload-section'),
      () => document.querySelector('input[type="file"][name*="properties"]')
    ];

    return indicators.some(check => {
      try {
        return check();
      } catch (e) {
        return false;
      }
    });
  };

  const isDoubleSidedVariant = () => {
    const section = addToCartForm?.closest('section') || addToCartForm?.closest('.product') || document;
    // Check radio buttons (Dawn theme variant selectors)
    const checkedRadios = section.querySelectorAll('input[type="radio"]:checked');
    for (const radio of checkedRadios) {
      if (radio.value === 'Double Sided') return true;
    }
    // Check select dropdowns
    const selects = section.querySelectorAll('select');
    for (const select of selects) {
      if (select.value === 'Double Sided') return true;
    }
    return false;
  };

  const checkPitchPrintProjectState = async () => {
    // Check for PitchPrint project
    const stateProjectId = stateManager.getProjectId();
    if (stateProjectId) {
      return true;
    }

    // Check for file upload
    const uploadData = stateManager.getUploadData();
    const uploadMode = stateManager.getUploadMode();
    if (uploadData || uploadMode === 'send_later') {
      return true;
    }

    const pitchPrintInput = document.querySelector('input[name="properties[_pitchprint]"]');
    const editButton = document.querySelector('#pp_edit_btn');

    const hasProjectId = pitchPrintInput && pitchPrintInput.value && pitchPrintInput.value.length > 10;
    const hasEditButton = editButton && editButton.style.display !== 'none';

    return hasProjectId || hasEditButton;
  };

  const getProjectId = async () => {
    const stateProjectId = stateManager.getProjectId();
    if (stateProjectId) return stateProjectId;

    const pitchPrintInput = document.querySelector('input[name="properties[_pitchprint]"]');
    if (pitchPrintInput && pitchPrintInput.value) {
      return pitchPrintInput.value;
    }

    return null;
  };

  // ── Shopify CDN Upload (for Cart Transform image override) ──
  const POPSIGNS_API = window.POPSIGNS_API_ENDPOINT || 'https://fz8y24ewil.execute-api.us-east-1.amazonaws.com/staging';

  // DISABLED 2026-06-17: The Cart Transform image override is off (no active Shopify
  // Plus) and these fire-and-forget uploads were only cluttering Shopify Files with
  // unused preview images. Set to true to re-enable the whole upload pipeline.
  const SHOPIFY_CDN_UPLOAD_ENABLED = false;

  const uploadToShopifyCdn = async (imageUrl, filename) => {
    if (!SHOPIFY_CDN_UPLOAD_ENABLED) return null;
    try {
      log('☁️ Uploading preview to Shopify CDN:', imageUrl);
      const response = await fetch(`${POPSIGNS_API}/api/uploads/shopify-cdn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl, filename }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        log('⚠️ Shopify CDN upload failed:', err);
        return null;
      }
      const { cdnUrl } = await response.json();
      log('✅ Shopify CDN URL:', cdnUrl);
      return cdnUrl;
    } catch (err) {
      log('⚠️ Shopify CDN upload error:', err);
      return null;
    }
  };

  const setShopifyPreviewUrl = (cdnUrl) => {
    if (!addToCartForm) return;
    let input = addToCartForm.querySelector('input[name="properties[_shopify_preview_url]"]');
    if (cdnUrl) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_shopify_preview_url]';
        addToCartForm.appendChild(input);
      }
      input.value = cdnUrl;
    } else if (input) {
      input.remove();
      log('🧹 Cleared stale Shopify CDN preview URL');
    }
  };

  const setProjectIdInForm = (projectId) => {
    if (!projectId || !addToCartForm) return;

    log('📝 Setting comprehensive PitchPrint data in form:', projectId);

    let input = addToCartForm.querySelector('input[name="properties[_pitchprint]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_pitchprint]';
      addToCartForm.appendChild(input);
    }
    input.value = projectId;

    // Original PDF download flag (kept for backward compatibility)
    let pdfInput = addToCartForm.querySelector('input[name="properties[_pprint-pdf-download]"]');
    if (!pdfInput) {
      pdfInput = document.createElement('input');
      pdfInput.type = 'hidden';
      pdfInput.name = 'properties[_pprint-pdf-download]';
      pdfInput.value = '1';
      addToCartForm.appendChild(pdfInput);
    }

    // Add PDF download URL (for admin/fulfillment)
    let pdfUrlInput = addToCartForm.querySelector('input[name="properties[_pitchprint_pdf_url]"]');
    if (!pdfUrlInput) {
      pdfUrlInput = document.createElement('input');
      pdfUrlInput.type = 'hidden';
      pdfUrlInput.name = 'properties[_pitchprint_pdf_url]';
      addToCartForm.appendChild(pdfUrlInput);
    }
    pdfUrlInput.value = `https://pdf.pitchprint.com/${projectId}`;

    // Add JPG preview URL (for quick previews)
    let jpgUrlInput = addToCartForm.querySelector('input[name="properties[_pitchprint_jpg_url]"]');
    if (!jpgUrlInput) {
      jpgUrlInput = document.createElement('input');
      jpgUrlInput.type = 'hidden';
      jpgUrlInput.name = 'properties[_pitchprint_jpg_url]';
      addToCartForm.appendChild(jpgUrlInput);
    }
    jpgUrlInput.value = `https://jpeg.pitchprint.com/${projectId}`;

    let systemInput = addToCartForm.querySelector('input[name="properties[_pp_system]"]');
    if (!systemInput) {
      systemInput = document.createElement('input');
      systemInput.type = 'hidden';
      systemInput.name = 'properties[_pp_system]';
      systemInput.value = 'unified_v4';
      addToCartForm.appendChild(systemInput);
    }

    // Add visible property for customer confirmation (cart and checkout)
    let artworkInput = addToCartForm.querySelector('input[name="properties[Artwork]"]');
    if (!artworkInput) {
      artworkInput = document.createElement('input');
      artworkInput.type = 'hidden';
      artworkInput.name = 'properties[Artwork]';
      artworkInput.value = '✓ Custom Design Saved';
      addToCartForm.appendChild(artworkInput);
    }

    // Add preview URL property for email integrations (Klaviyo) and checkout preview extension
    let previewUrlInput = addToCartForm.querySelector('input[name="properties[_pitchprint_preview_url]"]');
    if (!previewUrlInput) {
      previewUrlInput = document.createElement('input');
      previewUrlInput.type = 'hidden';
      previewUrlInput.name = 'properties[_pitchprint_preview_url]';
      previewUrlInput.value = `https://pitchprint.io/previews/${projectId}_1.jpg`;
      addToCartForm.appendChild(previewUrlInput);
    }

    // Add back preview URL for double-sided items
    if (isDoubleSidedVariant()) {
      let backPreviewInput = addToCartForm.querySelector('input[name="properties[_pitchprint_preview_url_back]"]');
      if (!backPreviewInput) {
        backPreviewInput = document.createElement('input');
        backPreviewInput.type = 'hidden';
        backPreviewInput.name = 'properties[_pitchprint_preview_url_back]';
        addToCartForm.appendChild(backPreviewInput);
      }
      backPreviewInput.value = `https://pitchprint.io/previews/${projectId}_2.jpg`;
    }

    // Upload preview to Shopify CDN for Cart Transform image override (fire-and-forget)
    const pitchprintPreviewUrl = `https://pitchprint.io/previews/${projectId}_1.jpg`;
    uploadToShopifyCdn(pitchprintPreviewUrl, `pp-${projectId}.jpg`).then(setShopifyPreviewUrl);

    log('✅ PitchPrint data set in form (hidden properties + visible custom artwork confirmation + preview URL)');
  };

  const setFileUploadDataInForm = (uploadData) => {
    if (!uploadData || !addToCartForm) return;

    log('📝 Setting file upload data in form:', uploadData);
    log('📝 Upload files object:', uploadData.files);

    // Set upload ID (used for tracking and cart preview lookup)
    let uploadIdInput = addToCartForm.querySelector('input[name="properties[_file_upload_id]"]');
    if (!uploadIdInput) {
      uploadIdInput = document.createElement('input');
      uploadIdInput.type = 'hidden';
      uploadIdInput.name = 'properties[_file_upload_id]';
      addToCartForm.appendChild(uploadIdInput);
    }
    uploadIdInput.value = uploadData.uploadId;

    // Set upload mode (upload_now or send_later)
    let modeInput = addToCartForm.querySelector('input[name="properties[_file_upload_mode]"]');
    if (!modeInput) {
      modeInput = document.createElement('input');
      modeInput.type = 'hidden';
      modeInput.name = 'properties[_file_upload_mode]';
      addToCartForm.appendChild(modeInput);
    }
    modeInput.value = uploadData.mode;

    // Set shape and sides info
    let shapeInput = addToCartForm.querySelector('input[name="properties[_file_upload_shape]"]');
    if (!shapeInput) {
      shapeInput = document.createElement('input');
      shapeInput.type = 'hidden';
      shapeInput.name = 'properties[_file_upload_shape]';
      addToCartForm.appendChild(shapeInput);
    }
    shapeInput.value = uploadData.shape;

    let sidesInput = addToCartForm.querySelector('input[name="properties[_file_upload_sides]"]');
    if (!sidesInput) {
      sidesInput = document.createElement('input');
      sidesInput.type = 'hidden';
      sidesInput.name = 'properties[_file_upload_sides]';
      addToCartForm.appendChild(sidesInput);
    }
    sidesInput.value = uploadData.sides;

    // Set system identifier
    let systemInput = addToCartForm.querySelector('input[name="properties[_pp_system]"]');
    if (!systemInput) {
      systemInput = document.createElement('input');
      systemInput.type = 'hidden';
      systemInput.name = 'properties[_pp_system]';
      addToCartForm.appendChild(systemInput);
    }
    systemInput.value = 'file_upload_v1';

    // Add visible property for customer confirmation
    let artworkInput = addToCartForm.querySelector('input[name="properties[Artwork]"]');
    if (!artworkInput) {
      artworkInput = document.createElement('input');
      artworkInput.type = 'hidden';
      artworkInput.name = 'properties[Artwork]';
      addToCartForm.appendChild(artworkInput);
    }

    if (uploadData.mode === 'send_later') {
      artworkInput.value = "✓ You're sending this later";
    } else {
      artworkInput.value = `✓ Custom Files Uploaded`;
    }

    // Add visible "Design Proof" property when proof is requested
    let proofInput = addToCartForm.querySelector('input[name="properties[Design Proof]"]');
    if (uploadData.proofRequested) {
      if (!proofInput) {
        proofInput = document.createElement('input');
        proofInput.type = 'hidden';
        proofInput.name = 'properties[Design Proof]';
        addToCartForm.appendChild(proofInput);
      }
      proofInput.value = '✓ Proof Requested';
    } else if (proofInput) {
      proofInput.remove();
    }

    // Add S3 file URLs for fulfillment team (visible properties)
    // AND preview URLs for mini-cart (hidden properties with underscore)
    log(`📝 Checking files object - has files: ${!!uploadData.files}, front: ${uploadData.files?.front}, back: ${uploadData.files?.back}`);

    // Safety: remove stale back inputs if single-sided (prevents carryover from previous orders)
    if (!uploadData.files?.back) {
      const staleBackInputs = addToCartForm.querySelectorAll(`
        input[name="properties[Back]"],
        input[name="properties[_back_file_url]"],
        input[name="properties[_back_file_cropped_url]"],
        input[name="properties[_file_upload_back_preview]"]
      `);
      staleBackInputs.forEach(input => {
        input.remove();
        log(`  🧹 Removed stale back input: ${input.name}`);
      });
    }

    if (uploadData.files && (uploadData.files.front || uploadData.files.back)) {
      if (uploadData.files.front) {
        // Use CDN domain for shorter, branded URLs
        const frontFileUrl = `https://img.popsigns.co/${uploadData.files.front}`;

        // Extract original filename from S3 key
        // NEW Format: uploads/{customerId}/original/{uuid}-{originalFilename}
        // OLD Format: uploads/{customerId}/original/{timestamp}-{uuid}-{originalFilename}
        const frontFileParts = uploadData.files.front.split('/');
        const frontFullFilename = frontFileParts[frontFileParts.length - 1];

        // Match pattern: {uuid}-{filename}
        // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (36 chars with dashes)
        // Remove UUID prefix: 36 chars + 1 dash = 37 chars
        const uuidLength = 36 + 1; // uuid-
        const frontOriginalFilename = frontFullFilename.substring(uuidLength);

        // Visible property: just the filename (cleaner for customer view in checkout)
        // Full URL stored in hidden property below for fulfillment team
        let frontFileInput = addToCartForm.querySelector('input[name="properties[Front]"]');
        if (!frontFileInput) {
          frontFileInput = document.createElement('input');
          frontFileInput.type = 'hidden';
          frontFileInput.name = 'properties[Front]';
          addToCartForm.appendChild(frontFileInput);
        }
        frontFileInput.value = frontOriginalFilename;
        log(`✅ Set Front file URL: ${frontOriginalFilename}`);

        // Hidden property: full URL for fulfillment team
        let frontUrlInput = addToCartForm.querySelector('input[name="properties[_front_file_url]"]');
        if (!frontUrlInput) {
          frontUrlInput = document.createElement('input');
          frontUrlInput.type = 'hidden';
          frontUrlInput.name = 'properties[_front_file_url]';
          addToCartForm.appendChild(frontUrlInput);
        }
        frontUrlInput.value = frontFileUrl;

        // Preview URL for mini-cart (hidden)
        let frontPreviewInput = addToCartForm.querySelector('input[name="properties[_file_upload_front_preview]"]');
        if (!frontPreviewInput) {
          frontPreviewInput = document.createElement('input');
          frontPreviewInput.type = 'hidden';
          frontPreviewInput.name = 'properties[_file_upload_front_preview]';
          addToCartForm.appendChild(frontPreviewInput);
        }
        frontPreviewInput.value = uploadData.previewUrls?.front || uploadData.croppedUrls?.front || frontFileUrl;

        // Upload preview to Shopify CDN for Cart Transform image override (fire-and-forget)
        const frontPreviewSource = uploadData.previewUrls?.front || uploadData.croppedUrls?.front;
        if (frontPreviewSource) {
          uploadToShopifyCdn(frontPreviewSource, `fu-${uploadData.uploadId}-front.png`).then(setShopifyPreviewUrl);
        }

        // Full-resolution cropped URL (shape-masked version for production + display)
        let frontCroppedInput = addToCartForm.querySelector('input[name="properties[_front_file_cropped_url]"]');
        if (uploadData.croppedUrls?.front) {
          if (!frontCroppedInput) {
            frontCroppedInput = document.createElement('input');
            frontCroppedInput.type = 'hidden';
            frontCroppedInput.name = 'properties[_front_file_cropped_url]';
            addToCartForm.appendChild(frontCroppedInput);
          }
          frontCroppedInput.value = uploadData.croppedUrls.front;
          log(`✅ Set Front cropped URL: ${uploadData.croppedUrls.front}`);
        } else if (frontCroppedInput) {
          frontCroppedInput.remove();
          log(`🧹 Cleared stale Front cropped URL (new crop pending)`);
        }
      }

      if (uploadData.files.back) {
        // Use CDN domain for shorter, branded URLs
        const backFileUrl = `https://img.popsigns.co/${uploadData.files.back}`;

        // Extract original filename from S3 key
        // Format: uploads/{customerId}/original/{uuid}-{originalFilename}
        // Each side gets its own uploadId, so no "back-" prefix in the key
        const backFileParts = uploadData.files.back.split('/');
        const backFullFilename = backFileParts[backFileParts.length - 1];

        // Match pattern: {uuid}-{filename}
        // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (36 chars with dashes)
        // Remove UUID prefix: 36 chars + 1 dash = 37 chars
        const uuidLength = 36 + 1; // uuid-
        const backOriginalFilename = backFullFilename.substring(uuidLength);

        // Visible property: just the filename (cleaner for customer view in checkout)
        // Full URL stored in hidden property below for fulfillment team
        let backFileInput = addToCartForm.querySelector('input[name="properties[Back]"]');
        if (!backFileInput) {
          backFileInput = document.createElement('input');
          backFileInput.type = 'hidden';
          backFileInput.name = 'properties[Back]';
          addToCartForm.appendChild(backFileInput);
        }
        backFileInput.value = backOriginalFilename;
        log(`✅ Set Back file URL: ${backOriginalFilename}`);

        // Hidden property: full URL for fulfillment team
        let backUrlInput = addToCartForm.querySelector('input[name="properties[_back_file_url]"]');
        if (!backUrlInput) {
          backUrlInput = document.createElement('input');
          backUrlInput.type = 'hidden';
          backUrlInput.name = 'properties[_back_file_url]';
          addToCartForm.appendChild(backUrlInput);
        }
        backUrlInput.value = backFileUrl;

        // Preview URL for mini-cart (hidden)
        let backPreviewInput = addToCartForm.querySelector('input[name="properties[_file_upload_back_preview]"]');
        if (!backPreviewInput) {
          backPreviewInput = document.createElement('input');
          backPreviewInput.type = 'hidden';
          backPreviewInput.name = 'properties[_file_upload_back_preview]';
          addToCartForm.appendChild(backPreviewInput);
        }
        backPreviewInput.value = uploadData.previewUrls?.back || uploadData.croppedUrls?.back || backFileUrl;

        // Full-resolution cropped URL (shape-masked version for production + display)
        let backCroppedInput = addToCartForm.querySelector('input[name="properties[_back_file_cropped_url]"]');
        if (uploadData.croppedUrls?.back) {
          if (!backCroppedInput) {
            backCroppedInput = document.createElement('input');
            backCroppedInput.type = 'hidden';
            backCroppedInput.name = 'properties[_back_file_cropped_url]';
            addToCartForm.appendChild(backCroppedInput);
          }
          backCroppedInput.value = uploadData.croppedUrls.back;
          log(`✅ Set Back cropped URL: ${uploadData.croppedUrls.back}`);
        } else if (backCroppedInput) {
          backCroppedInput.remove();
          log(`🧹 Cleared stale Back cropped URL (new crop pending)`);
        }
      }
    }

    // Verify all inputs are present
    const allInputs = addToCartForm.querySelectorAll('input[name^="properties"]');
    log(`📝 Total property inputs in form: ${allInputs.length}`);
    allInputs.forEach(input => {
      log(`  - ${input.name}: ${input.value.substring(0, 50)}${input.value.length > 50 ? '...' : ''}`);
    });

    log('✅ File upload data set in form (upload ID, mode, shape, sides, S3 URLs)');
  };

  const getHumanFriendlyErrorMessage = (error) => {
    const message = error.message || error.toString();

    if (message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network')) {
      return 'Connection issue. Please check your internet and try again.';
    }
    if (message.toLowerCase().includes('inventory') || message.toLowerCase().includes('stock')) {
      return 'This item may be out of stock. Please refresh the page.';
    }

    return 'Something went wrong. Please try again or contact support.';
  };

  const triggerCustomizationPrompt = () => {
    // Check if we're in file upload mode
    const designMethod = stateManager?.getDesignMethod ? stateManager.getDesignMethod() : null;

    if (designMethod === 'file_upload') {
      // File upload mode - scroll to upload section and add validation styling
      const fileUploadContainer = document.querySelector('.custom-file-uploader-wrapper, #custom-file-uploader-container, .custom-file-uploader, .file-upload-wrapper');
      if (fileUploadContainer) {
        // Add validation error class to show purple gradient and badge
        const wrapper = document.querySelector('.custom-file-uploader-wrapper');
        if (wrapper) {
          wrapper.classList.add('validation-error');

          // Remove error state when user interacts with upload section
          const removeError = () => {
            wrapper.classList.add('interaction-started');
            setTimeout(() => {
              wrapper.classList.remove('validation-error', 'interaction-started');
            }, 300);
            wrapper.removeEventListener('click', removeError);
            wrapper.removeEventListener('change', removeError);
          };

          wrapper.addEventListener('click', removeError, { once: true });
          wrapper.addEventListener('change', removeError, { once: true });
        }

        // Scroll to the upload section
        fileUploadContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Add pulse animation to match enhanced integration behavior
        fileUploadContainer.style.animation = 'pulse 1s ease-in-out 2';

        log('📁 Scrolled to file upload section with validation styling');
      }
    } else {
      // PitchPrint mode - click the customize button
      const customizeBtn = document.querySelector('#pp_customize, #pp_main_btn, .pitchprint-customize-btn');
      if (customizeBtn) {
        // Dispatch analytics event before programmatic click so analytics knows this was user-initiated
        document.dispatchEvent(new CustomEvent('pitchprint:editor:launched', {
          bubbles: true,
          detail: { source: 'sticky_atc' }
        }));
        customizeBtn.click();
      }
    }
  };

  const clearValidationCache = () => {
    validationCache.clear();
  };

  const clearPitchPrintProperties = () => {
    if (!addToCartForm) return;

    log('🧹 Clearing PitchPrint properties from form');

    // Remove ALL PitchPrint properties
    const pitchprintInputs = addToCartForm.querySelectorAll(`
      input[name*="pitchprint"],
      input[name*="_pitchprint"],
      input[name*="pprint"]
    `);
    pitchprintInputs.forEach(input => {
      input.remove();
      log(`  ❌ Removed: ${input.name}`);
    });

    log('✅ PitchPrint properties cleared from form');
  };

  const clearFileUploadProperties = () => {
    if (!addToCartForm) return;

    log('🧹 Clearing file upload properties from form');

    // Remove ALL file upload properties (including cropped URLs)
    const fileUploadInputs = addToCartForm.querySelectorAll(`
      input[name*="file_upload"],
      input[name*="_file_upload"],
      input[name="properties[Front]"],
      input[name="properties[Back]"],
      input[name="properties[_front_file_url]"],
      input[name="properties[_back_file_url]"],
      input[name="properties[_front_file_cropped_url]"],
      input[name="properties[_back_file_cropped_url]"],
      input[name="properties[_file_upload_front_preview]"],
      input[name="properties[_file_upload_back_preview]"],
      input[name="properties[Design Proof]"]
    `);
    fileUploadInputs.forEach(input => {
      input.remove();
      log(`  ❌ Removed: ${input.name}`);
    });

    log('✅ File upload properties cleared from form');
  };

  const clearAllDesignProperties = () => {
    if (!addToCartForm) return;

    log('🧹 Clearing ALL design properties from form (PitchPrint + File Upload)');

    clearPitchPrintProperties();
    clearFileUploadProperties();

    // Remove artwork property (will be re-added by active method)
    const artworkInput = addToCartForm.querySelector('input[name="properties[Artwork]"]');
    if (artworkInput) {
      artworkInput.remove();
      log('  ❌ Removed: properties[Artwork]');
    }

    log('✅ All design properties cleared from form');
  };

  // =====================================
  // CRITICAL FIX: ADD-TO-CART COMPLETION MONITORING
  // =====================================

  // In your assets/add-to-cart-unified.js file
  // Find the setupAddToCartCompletionMonitor function (around line 180)
  // Replace it with this enhanced version that handles toast flagging:

  const setupAddToCartCompletionMonitor = (projectId) => {
    log('🔍 Setting up add-to-cart completion monitor for project:', projectId);

    let cartCompletionDetected = false;
    let projectClearingDetected = false;

    // Monitor for cart addition (via multiple methods)
    const cartMonitor = setInterval(() => {
      // Check if cart was updated (various cart APIs)
      if (window.theme && window.theme.cart && window.theme.cart.itemCount) {
        cartCompletionDetected = true;
      }

      // Check for Shopify cart API responses
      const cartDrawer = document.querySelector('.cart-drawer, .mini-cart');
      if (cartDrawer && cartDrawer.style.display !== 'none') {
        cartCompletionDetected = true;
      }

      // Listen for cart events
      if (cartCompletionDetected && !projectClearingDetected) {
        log('🛒 Cart addition detected, checking if PitchPrint cleared project...');

        // FIXED: Set toast flags for post-reload success toast
        setPostReloadToastFlags();

        // Give PitchPrint 2 seconds to handle clearing naturally
        setTimeout(() => {
          checkAndForceClearIfNeeded(projectId);
        }, 2000);

        clearInterval(cartMonitor);
      }
    }, 500);

    // Also listen for cart events
    const cartEventHandler = (e) => {
      log('📡 Cart event detected:', e.type);
      cartCompletionDetected = true;

      // FIXED: Set toast flags for post-reload success toast
      setPostReloadToastFlags();

      setTimeout(() => {
        checkAndForceClearIfNeeded(projectId);
      }, 2000);

      document.removeEventListener('cart:updated', cartEventHandler);
      document.removeEventListener('cart:item-added', cartEventHandler);
    };

    document.addEventListener('cart:updated', cartEventHandler);
    document.addEventListener('cart:item-added', cartEventHandler);

    // Cleanup monitor after 10 seconds
    setTimeout(() => {
      clearInterval(cartMonitor);
      document.removeEventListener('cart:updated', cartEventHandler);
      document.removeEventListener('cart:item-added', cartEventHandler);
    }, 10000);
  };

  // ADD THIS NEW FUNCTION right after the setupAddToCartCompletionMonitor function:

  const setPostReloadToastFlags = () => {
    // Check if addToCartSuccess toast is enabled
    if (window.TOAST_CONFIG &&
        window.TOAST_CONFIG.enabled &&
        window.TOAST_CONFIG.messages.addToCartSuccess) {

      log('🎯 Setting post-reload toast flags for addToCartSuccess');

      // Set flags for post-reload toast
      sessionStorage.setItem('pitchprint_show_success_toast', 'true');
      sessionStorage.setItem('pitchprint_success_timestamp', Date.now().toString());

      log('✅ Post-reload toast flags set successfully');
    } else {
      log('🔇 addToCartSuccess toast disabled, skipping flags');
    }
  };

  const checkAndForceClearIfNeeded = async (originalProjectId) => {
    log('🔍 Checking if project clearing is needed...');

    try {
      // Check if PitchPrint naturally cleared the project
      const currentProjectId = await getProjectId();
      const stateProjectId = stateManager.getProjectId();

      log('📊 Project clearing check:', {
        original: originalProjectId,
        current: currentProjectId,
        state: stateProjectId
      });

      // If project is still active, PitchPrint didn't clear it naturally
      if ((currentProjectId && currentProjectId === originalProjectId) ||
          (stateProjectId && stateProjectId === originalProjectId)) {

        log('🧹 PitchPrint did not clear project naturally - forcing clear...');

        // Force clear the project using multiple methods
        await forceClearPitchPrintProject(originalProjectId);

        // Also clear our state
        if (stateManager) {
          stateManager.clearProject();
        }

        // Optionally refresh the page to match PitchPrint's natural behavior
        setTimeout(() => {
          log('🔄 Refreshing page to complete the add-to-cart cycle...');
          window.location.reload();
        }, 1000);

      } else {
        log('✅ PitchPrint handled project clearing naturally');
      }

    } catch (error) {
      log('❌ Error during project clearing check:', error);
    }
  };

  const forceClearPitchPrintProject = async (projectId) => {
    log('🧹 Force clearing PitchPrint project:', projectId);

    try {
      // Method 1: Use PitchPrint's clear design button
      const clearButton = document.querySelector('#pp_clear_design_btn');
      if (clearButton && clearButton.style.display !== 'none') {
        log('🔄 Clicking PitchPrint clear button');
        clearButton.click();
        return;
      }

      // Method 2: Use PitchPrint API if available
      if (window.ppclient && window.ppclient.fire) {
        log('🔄 Using PitchPrint API to clear project');
        window.ppclient.fire('clear-design');

        // Also clear the project ID from PitchPrint's internal state
        if (window.ppclient._vars) {
          window.ppclient._vars.projectId = null;
        }
      }

      // Method 3: Clear form inputs manually
      const pitchPrintInputs = document.querySelectorAll(`
        input[name*="pitchprint"],
        input[name*="_pitchprint"],
        input[name*="pprint"]
      `);

      pitchPrintInputs.forEach(input => {
        input.value = '';
        log('🧹 Cleared input:', input.name);
      });

      // Method 4: Hide PitchPrint UI elements
      const editButton = document.querySelector('#pp_edit_btn');
      if (editButton) {
        editButton.style.display = 'none';
      }

      const pdfButton = document.querySelector('#_pprint-pdf-download');
      if (pdfButton) {
        pdfButton.style.display = 'none';
      }

      log('✅ Forced project clearing completed');

    } catch (error) {
      log('❌ Error during forced project clearing:', error);
    }
  };

  // Unified button states
  const UNIFIED_STATES = {
    NEED_DESIGN: 'need_design',
    SENDING_LATER: 'sending_later',
    READY_TO_ADD: 'ready_to_add',
    STANDARD_PRODUCT: 'standard_product', // Hardware-only variants - no customization required
    VALIDATING: 'validating',
    PROCESSING: 'processing',
    SUCCESS: 'success',
    ERROR: 'error',
    DISABLED: 'disabled'
  };

  // State configuration
  const STATE_CONFIG = {
    [UNIFIED_STATES.NEED_DESIGN]: {
      buttonText: 'Start Designing First',
      buttonEnabled: true, // Changed to true - button should be clickable
      buttonClass: 'need-design',
      headerTitle: '🎨 Ready to Design?',
      headerSubtitle: 'Click "Start Designing" above to choose your template and customize your sign! 👆',
      headerOverline: 'NEXT STEP'
    },
    [UNIFIED_STATES.SENDING_LATER]: {
      buttonText: 'Add to Cart',
      buttonEnabled: true,
      buttonClass: 'sending-later',
      headerTitle: '📧 Files Will Be Sent Later',
      headerSubtitle: 'We\'ll reach out after your order to collect your artwork',
      headerOverline: 'FINAL STEP'
    },
    [UNIFIED_STATES.READY_TO_ADD]: {
      buttonText: 'Add to Cart',
      buttonEnabled: true,
      buttonClass: 'ready-to-add',
      headerTitle: '🎉 You\'re Almost There!',
      headerSubtitle: 'Your custom design is ready • Let\'s make it official! ✨',
      headerOverline: 'FINAL STEP'
    },
    [UNIFIED_STATES.STANDARD_PRODUCT]: {
      buttonText: 'Add to Cart',
      buttonEnabled: true,
      buttonClass: 'standard-product',
      headerTitle: '🛒 Ready to Order!',
      headerSubtitle: 'This item is ready to add to your cart',
      headerOverline: 'FINAL STEP'
    },
    [UNIFIED_STATES.VALIDATING]: {
      buttonText: 'Checking Design...',
      buttonEnabled: false,
      buttonClass: 'validating',
      headerTitle: '🔍 Validating Design',
      headerSubtitle: 'Making sure everything looks perfect...',
      headerOverline: 'PROCESSING...'
    },
    [UNIFIED_STATES.PROCESSING]: {
      buttonText: 'Adding to Cart...',
      buttonEnabled: false,
      buttonClass: 'processing',
      headerTitle: '⚡ Almost Done!',
      headerSubtitle: 'Adding your custom design to cart...',
      headerOverline: 'FINAL STEP'
    },
    [UNIFIED_STATES.SUCCESS]: {
      buttonText: 'Added Successfully!',
      buttonEnabled: false,
      buttonClass: 'success',
      headerTitle: '✅ Success!',
      headerSubtitle: 'Your custom design has been added to cart!',
      headerOverline: 'Complete'
    },
    [UNIFIED_STATES.ERROR]: {
      buttonText: 'Try Again',
      buttonEnabled: true,
      buttonClass: 'error',
      headerTitle: '⚠️ Let\'s Try Again',
      headerSubtitle: 'Something went wrong. Please try again or contact support.',
      headerOverline: 'RUH ROH!'
    },
    [UNIFIED_STATES.DISABLED]: {
      buttonText: 'Unavailable',
      buttonEnabled: false,
      buttonClass: 'disabled',
      headerTitle: '🚫 Temporarily Unavailable',
      headerSubtitle: 'Please refresh the page and try again.',
      headerOverline: 'RUH ROH!'
    }
  };

  // =====================================
  // INITIALIZATION & SETUP
  // =====================================

  const init = () => {
    if (initialized) {
      log('Already initialized, skipping...');
      return;
    }
    initialized = true;
    log('🚀 Initializing Unified Add-to-Cart System...');

    if (!findFormAndButton()) {
      log('❌ Cannot initialize without form/button - will retry...');
      setTimeout(init, 500);
      return;
    }

    calculateStepNumber();
    injectUnifiedStyles();
    setupStateManagerListeners();
    setupFormHandling();
    setupPitchPrintIntegration();
    setupCartCountMonitor(); // Monitor cart for successful additions

    setTimeout(() => {
      performInitialStateAnalysis();
      injectCelebrationHeader();
    }, 100);

    log('✅ Unified Add-to-Cart System ready');
  };

  // Enhanced form and button detection
  const findFormAndButton = () => {
    const cacheKey = 'form_button_detection';
    if (validationCache.has(cacheKey)) {
      const cached = validationCache.get(cacheKey);
      addToCartForm = document.querySelector(cached.formSelector);
      addToCartButton = document.querySelector(cached.buttonSelector);

      if (addToCartForm && addToCartForm instanceof HTMLFormElement &&
          addToCartButton && addToCartButton instanceof HTMLElement) {
        log('✅ Using cached form/button selectors');
        return true;
      } else {
        log('⚠️ Cached selectors invalid, clearing cache');
        validationCache.delete(cacheKey);
      }
    }

    const formSelectors = [
      '.product-form form',
      'form.product-form',
      '.product-form',
      'form[action*="/cart/add"]',
      'form[action="/cart/add"]'
    ];

    const buttonSelectors = [
      '.product-form__submit',
      'button[name="add"]',
      'button[type="submit"]',
      '.btn[type="submit"]'
    ];

    let bestForm = null;
    let bestButton = null;
    let bestFormSelector = '';
    let bestButtonSelector = '';

    for (const selector of formSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        if (element.tagName === 'FORM') {
          bestForm = element;
          bestFormSelector = selector;
          break;
        } else {
          const nestedForm = element.querySelector('form');
          if (nestedForm) {
            bestForm = nestedForm;
            bestFormSelector = `${selector} form`;
            break;
          }
        }
      }
    }

    for (const selector of buttonSelectors) {
      const button = document.querySelector(selector);
      if (button && button instanceof HTMLElement) {
        bestButton = button;
        bestButtonSelector = selector;
        break;
      }
    }

    addToCartForm = bestForm;
    addToCartButton = bestButton;

    if (addToCartForm && addToCartForm instanceof HTMLFormElement &&
        addToCartButton && addToCartButton instanceof HTMLElement) {

      validationCache.set(cacheKey, {
        formSelector: bestFormSelector,
        buttonSelector: bestButtonSelector,
        timestamp: Date.now()
      });

      originalButtonText = addToCartButton.textContent.trim();
      addToCartButton.dataset.originalText = originalButtonText;
      addToCartButton.dataset.unifiedManaged = 'true';

      log(`✅ Enhanced detection - Form: ${bestFormSelector}, Button: ${bestButtonSelector}`);
      return true;
    }

    log('❌ Enhanced form/button detection failed');
    return false;
  };

  const calculateStepNumber = () => {
    // Use empty string - headerOverline templates will be changed to "FINAL STEP"
    stepNumber = '';
    log(`📊 Using "FINAL STEP" static text in headerOverline`);
  };

  // =====================================
  // FORM HANDLING - FIXED WITH COMPLETION MONITORING
  // =====================================

  const setupFormHandling = () => {
    if (!addToCartForm || !addToCartButton) return;

    // Only attach form submission handler for products that need customization
    // (PitchPrint or file upload). Simple products are handled by the theme's
    // ProductForm custom element in global.js — attaching here would double-add.
    const needsUnifiedHandler = isPitchPrintProduct() || isFileUploadProduct();
    if (!needsUnifiedHandler) {
      log('⏭️ Simple product — skipping form submission handler (theme handles it)');
    } else {
      addToCartForm.addEventListener('submit', handleFormSubmission);
    }
    addToCartButton.addEventListener('click', handleButtonClick);

    addToCartButton.addEventListener('click', () => {
      performanceMetrics.buttonInteractions++;
    });

    log('📋 Form handlers attached');
  };

  const handleFormSubmission = async (e) => {
    log('📋 Form submission intercepted');

    // FIXED: Now isPitchPrintProduct is properly defined above
    const hasActiveProject = await checkPitchPrintProjectState();
    const isPitchPrintProd = isPitchPrintProduct();

    if (isPitchPrintProd && hasActiveProject) {
      log('🎨 PitchPrint product with active project - allowing PitchPrint to handle natural add to cart flow');

      // Check for file upload or PitchPrint project
      const uploadData = stateManager.getUploadData();
      const projectId = await getProjectId();

      // CRITICAL: Let PitchPrint and File Upload handle their own properties
      // We only set properties for file upload, PitchPrint sets its own
      if (uploadData) {
        // File upload flow - set our properties
        setFileUploadDataInForm(uploadData);
        log('📝 File upload data set in form for natural flow');
        setupAddToCartCompletionMonitor(uploadData.uploadId);
      } else if (projectId) {
        // PitchPrint flow - DO NOT touch properties, PitchPrint has already set them
        log('📝 PitchPrint project detected, allowing natural flow without interference');
        setupAddToCartCompletionMonitor(projectId);
      }

      return; // Let the form submit naturally
    }

    log('🔄 Using unified add-to-cart system for non-PitchPrint product or product without project');

    const startTime = performance.now();

    try {
      setState(UNIFIED_STATES.VALIDATING);
      await new Promise(resolve => setTimeout(resolve, 300));

      const validation = await performValidation();
      if (!validation.isValid) {
        e.preventDefault();
        setState(UNIFIED_STATES.ERROR, true, validation.message);
        return;
      }

      e.preventDefault();

      setState(UNIFIED_STATES.PROCESSING);
      const result = await submitToCart(validation.projectData);

      if (result.success) {
        setState(UNIFIED_STATES.SUCCESS, true, 'Successfully added to cart!');

        performanceMetrics.submissionTimes.push(performance.now() - startTime);

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: result.data
        }));

        setTimeout(() => {
          setState(UNIFIED_STATES.READY_TO_ADD);
        }, 2000);

      } else {
        throw new Error(result.error || 'Submission failed');
      }

    } catch (error) {
      log('❌ Unified submission failed:', error);
      performanceMetrics.errorCounts++;
      stateManager.logError(error, 'handleFormSubmission');

      setState(UNIFIED_STATES.ERROR, true, getHumanFriendlyErrorMessage(error));

      setTimeout(() => {
        performInitialStateAnalysis();
      }, 3000);
    }
  };

  const handleButtonClick = async (e) => {
    if (currentState === UNIFIED_STATES.NEED_DESIGN) {
      e.preventDefault();
      triggerCustomizationPrompt();
      // showUnifiedToast('info', 'Please start designing your product first'); // Commented out - button now opens designer directly
      return;
    }

    if (!STATE_CONFIG[currentState]?.buttonEnabled) {
      e.preventDefault();
      log('🚫 Button click blocked - button disabled');
      return;
    }

    log('✅ Button click allowed');
  };

  // =====================================
  // VALIDATION & SUBMISSION
  // =====================================

  const performValidation = async () => {
    log('🔍 Enhanced validation starting...');

    if (!addToCartForm) {
      log('❌ Form validation failed: addToCartForm is null');
      return {
        isValid: false,
        message: 'Add to cart form not found. Please refresh the page.'
      };
    }

    if (!(addToCartForm instanceof HTMLFormElement)) {
      log('❌ Form validation failed: addToCartForm is not an HTMLFormElement');
      return {
        isValid: false,
        message: 'Add to cart form is invalid. Please refresh the page.'
      };
    }

    let formData;
    try {
      formData = new FormData(addToCartForm);
      log('✅ FormData created successfully');
    } catch (formDataError) {
      log('❌ FormData creation failed:', formDataError);

      const fallbackForm = document.querySelector('.product-form form, form[action*="/cart/add"]');
      if (fallbackForm && fallbackForm instanceof HTMLFormElement) {
        try {
          formData = new FormData(fallbackForm);
          addToCartForm = fallbackForm;
          log('✅ FormData created with fallback form');
        } catch (fallbackError) {
          log('❌ Fallback FormData creation also failed:', fallbackError);
          return {
            isValid: false,
            message: 'Unable to process form data. Please refresh the page and try again.'
          };
        }
      } else {
        return {
          isValid: false,
          message: 'Form validation failed. Please refresh the page and try again.'
        };
      }
    }

    const hasProject = await checkPitchPrintProjectState();
    const requiresCustomization = isPitchPrintProduct();
    const usesFileUpload = isFileUploadProduct();
    const uploadMode = stateManager?.getUploadMode ? stateManager.getUploadMode() : null;
    const uploadData = stateManager?.getUploadData ? stateManager.getUploadData() : null;

    log('🔍 Validation state:', {
      hasProject,
      requiresCustomization,
      usesFileUpload,
      uploadMode,
      hasUploadData: !!uploadData,
      formValid: !!formData
    });

    // Check if file upload is incomplete (upload_now mode without files OR no mode selected)
    const fileUploadIncomplete = usesFileUpload &&
                                  uploadMode !== 'send_later' &&
                                  !uploadData;

    if (requiresCustomization && !hasProject) {
      // Add visual validation error to file upload container if applicable
      const fileUploadContainer = document.querySelector('.custom-file-uploader-wrapper');
      if (fileUploadContainer && fileUploadIncomplete) {
        fileUploadContainer.classList.add('validation-error');

        // Scroll to the error
        fileUploadContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Remove error state when user interacts with upload section
        const removeError = () => {
          fileUploadContainer.classList.add('interaction-started');
          setTimeout(() => {
            fileUploadContainer.classList.remove('validation-error', 'interaction-started');
          }, 300);
          fileUploadContainer.removeEventListener('click', removeError);
          fileUploadContainer.removeEventListener('change', removeError);
        };

        fileUploadContainer.addEventListener('click', removeError, { once: true });
        fileUploadContainer.addEventListener('change', removeError, { once: true });
      }

      return {
        isValid: false,
        message: 'Please customize your design first'
      };
    }

    const variantId = formData.get('id');
    if (!variantId) {
      return {
        isValid: false,
        message: 'Please select a product variant'
      };
    }

    log('✅ Validation passed');
    return {
      isValid: true,
      projectData: hasProject ? { projectId: await getProjectId() } : null
    };
  };

  const submitToCart = async (projectData) => {
    log('🚀 Enhanced cart submission with verified PitchPrint data...');

    try {
      let formData;
      try {
        formData = new FormData(addToCartForm);
      } catch (formDataError) {
        log('❌ FormData constructor failed:', formDataError);
        throw new Error('Failed to create FormData: ' + formDataError.message);
      }

      if (projectData?.projectId) {
        log('📝 Setting PitchPrint data (hidden + visible custom artwork property):', projectData.projectId);

        setProjectIdInForm(projectData.projectId);

        // Hidden properties for system tracking
        formData.set('properties[_pitchprint]', projectData.projectId);
        formData.set('properties[_pprint-pdf-download]', '1');
        formData.set('properties[_pp_system]', 'unified_v4');

        // Download URLs for fulfillment team
        formData.set('properties[_pitchprint_pdf_url]', `https://pdf.pitchprint.com/${projectData.projectId}`);
        formData.set('properties[_pitchprint_jpg_url]', `https://jpeg.pitchprint.com/${projectData.projectId}`);

        // Visible property for customer confirmation (cart and checkout)
        formData.set('properties[Artwork]', '✓ Custom Design Saved');

        // Preview URL for email integrations (Klaviyo) and checkout preview extension
        formData.set('properties[_pitchprint_preview_url]', `https://pitchprint.io/previews/${projectData.projectId}_1.jpg`);

        // Back preview URL for double-sided items
        if (isDoubleSidedVariant()) {
          formData.set('properties[_pitchprint_preview_url_back]', `https://pitchprint.io/previews/${projectData.projectId}_2.jpg`);
        }

        // Shopify CDN preview URL (for Cart Transform image override)
        // This was set async by setProjectIdInForm — pick it up from the hidden input
        const shopifyCdnInput = addToCartForm?.querySelector('input[name="properties[_shopify_preview_url]"]');
        if (shopifyCdnInput?.value) {
          formData.set('properties[_shopify_preview_url]', shopifyCdnInput.value);
        }

        if (window.PITCHPRINT_DEBUG) {
          log('📋 PitchPrint FormData verification:');
          const pitchPrintProps = [];
          for (let [key, value] of formData.entries()) {
            if (key.includes('properties') && key.includes('pitchprint')) {
              pitchPrintProps.push(`${key}: ${value}`);
            }
          }
          console.log('  PitchPrint properties:', pitchPrintProps);
        }
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json',
          'X-PitchPrint-System': 'unified_v4'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (projectData?.projectId && window.PITCHPRINT_DEBUG) {
        verifyCartResult(result, projectData.projectId);
      }

      return { success: true, data: result };

    } catch (error) {
      log('❌ Enhanced cart submission failed:', error);
      return { success: false, error: error.message };
    }
  };

  const verifyCartResult = (cartData, expectedProjectId) => {
    log('🔍 Verifying PitchPrint data in cart response...');

    if (!cartData || !cartData.properties) {
      log('⚠️ No properties in cart response');
      return;
    }

    const properties = cartData.properties;
    const pitchPrintId = properties._pitchprint || properties.pitchprint;

    if (pitchPrintId === expectedProjectId) {
      log('✅ PitchPrint data successfully added to cart:', pitchPrintId);
    } else {
      log('❌ PitchPrint data mismatch in cart:', {
        expected: expectedProjectId,
        found: pitchPrintId,
        allProperties: properties
      });
    }

    Object.keys(properties).forEach(key => {
      if (key.includes('pitchprint') || key.includes('pprint') || key.includes('pp_')) {
        log(`📋 Cart property: ${key} = ${properties[key]}`);
      }
    });
  };

  // =====================================
  // STATE MANAGEMENT
  // =====================================

  const setState = (newState, showToast = false, toastMessage = '') => {
    if (currentState === newState) return;

    const oldState = currentState;
    currentState = newState;
    performanceMetrics.stateChanges++;

    log(`🎛️ State change: ${oldState} → ${newState}`);

    updateButtonState();
    updateCelebrationHeader();

    if (showToast && toastMessage) {
      showUnifiedToast(newState, toastMessage);
    }

    document.dispatchEvent(new CustomEvent('unified-addtocart:state-changed', {
      detail: { oldState, newState, config: STATE_CONFIG[newState] }
    }));
  };

  const updateButtonState = () => {
    if (!addToCartButton || !STATE_CONFIG[currentState]) return;

    let config = STATE_CONFIG[currentState];

    // Override button text for NEED_DESIGN state if in file upload mode
    if (currentState === UNIFIED_STATES.NEED_DESIGN) {
      const designMethod = stateManager?.getDesignMethod ? stateManager.getDesignMethod() : null;
      if (designMethod === 'file_upload') {
        config = {
          ...config,
          buttonText: 'Next: Upload Your Files'
        };
      }
    }

    addToCartButton.disabled = !config.buttonEnabled;

    // CRITICAL: Check if enhanced integration is managing this button
    // If the button has the enhanced structure, the enhanced system will handle text updates
    const hasEnhancedStructure = addToCartButton.querySelector('.enhanced-cart-content');
    const isEnhancedButton = addToCartButton.id === 'main-enhanced-cart-button';

    if (hasEnhancedStructure && isEnhancedButton) {
      // Enhanced integration is active - let it handle text updates
      log(`🔘 Button managed by enhanced integration, skipping text update`);
    } else {
      // Legacy button without enhanced structure
      // Check if there's a .btn-text element to update
      const btnTextElement = addToCartButton.querySelector('.btn-text');
      if (btnTextElement) {
        // Button has text element - update it
        btnTextElement.textContent = config.buttonText;
        log(`🔘 Button text updated via .btn-text element: ${config.buttonText}`);
      } else {
        // No enhanced structure - update button text directly (legacy fallback)
        // Only do this if button doesn't have complex structure
        if (!addToCartButton.querySelector('.enhanced-cart-content')) {
          addToCartButton.textContent = config.buttonText;
          log(`🔘 Button text updated directly (legacy): ${config.buttonText}`);
        }
      }
    }

    Object.values(UNIFIED_STATES).forEach(state => {
      const className = STATE_CONFIG[state]?.buttonClass;
      if (className) {
        addToCartButton.classList.remove(className);
      }
    });
    addToCartButton.classList.add(config.buttonClass);

    addToCartButton.setAttribute('aria-label', config.buttonText);
    addToCartButton.setAttribute('data-unified-state', currentState);

    log(`🔘 Button state updated: ${currentState} (${config.buttonEnabled ? 'enabled' : 'disabled'})`);
  };

  const updateCelebrationHeader = () => {
    if (!celebrationHeader || !STATE_CONFIG[currentState]) return;

    // Recalculate step number to ensure accuracy based on current DOM
    calculateStepNumber();

    let config = STATE_CONFIG[currentState];

    // Override header for NEED_DESIGN state if in file upload mode
    if (currentState === UNIFIED_STATES.NEED_DESIGN) {
      const designMethod = stateManager?.getDesignMethod ? stateManager.getDesignMethod() : null;
      if (designMethod === 'file_upload') {
        config = {
          ...config,
          headerTitle: '📁 Files Required',
          headerSubtitle: 'Please upload your artwork files to continue'
        };
      }
    }

    performanceMetrics.headerUpdates++;

    celebrationHeader.classList.add('updating');

    setTimeout(() => {
      const overlineElement = celebrationHeader.querySelector('.step-overline');
      const titleElement = celebrationHeader.querySelector('.celebration-main-title');
      const subtitleElement = celebrationHeader.querySelector('.celebration-subtitle');

      if (overlineElement) {
        overlineElement.textContent = config.headerOverline;
      }
      if (titleElement) {
        titleElement.textContent = config.headerTitle;
      }
      if (subtitleElement) {
        subtitleElement.textContent = config.headerSubtitle;
      }

      celebrationHeader.classList.remove('updating');

      log(`🎊 Header updated: ${config.headerTitle}`);
    }, 100);
  };

  const performInitialStateAnalysis = async () => {
    const hasProject = await checkPitchPrintProjectState();
    const requiresCustomization = isPitchPrintProduct();

    // Check current design method
    const designMethod = stateManager?.getDesignMethod ? stateManager.getDesignMethod() : null;
    const isFileUpload = designMethod === 'file_upload';

    log('🔍 Initial state analysis:', {
      hasProject,
      requiresCustomization,
      designMethod,
      isFileUpload,
      currentState
    });

    if (requiresCustomization && !hasProject) {
      setState(UNIFIED_STATES.NEED_DESIGN);
    } else if (hasProject) {
      setState(UNIFIED_STATES.READY_TO_ADD);
    } else {
      setState(UNIFIED_STATES.READY_TO_ADD);
    }
  };

  // =====================================
  // REMAINING FUNCTIONS (UNIFIED STYLING, HEADER, PITCHPRINT INTEGRATION, ETC.)
  // =====================================

  const injectUnifiedStyles = () => {
    if (document.getElementById('unified-add-to-cart-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'unified-add-to-cart-styles';
    styles.textContent = `
      .product-form__submit.need-design {
        background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%) !important;
        color: rgba(255, 255, 255, 0.8) !important;
        transform: none !important;
      }

      .product-form__submit.ready-to-add {
        background: linear-gradient(135deg, #6f08fb 0%, #5b21b6 100%) !important;
        color: white !important;
        cursor: pointer !important;
        animation: pulse-ready 2s ease-in-out infinite !important;
      }

      .product-form__submit.validating {
        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
        color: white !important;
        cursor: not-allowed !important;
        animation: pulse-validating 1.5s ease-in-out infinite !important;
      }

      .product-form__submit.processing {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
        color: white !important;
        cursor: not-allowed !important;
        animation: shimmer-processing 2s linear infinite !important;
      }

      .product-form__submit.success {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
        color: white !important;
        cursor: not-allowed !important;
        animation: bounce-success 0.6s ease-out !important;
      }

      .product-form__submit.error {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
        color: white !important;
        cursor: pointer !important;
        animation: shake-error 0.5s ease-in-out !important;
      }

      @keyframes pulse-ready {
        0%, 100% {
          box-shadow: 0 8px 25px rgba(111, 8, 251, 0.3);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 12px 35px rgba(111, 8, 251, 0.5);
          transform: scale(1.02);
        }
      }

      @keyframes pulse-validating {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }

      @keyframes shimmer-processing {
        0% { background-position: -200px 0; }
        100% { background-position: 200px 0; }
      }

      @keyframes bounce-success {
        0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
        40%, 43% { transform: translate3d(0,-8px,0); }
        70% { transform: translate3d(0,-4px,0); }
        90% { transform: translate3d(0,-2px,0); }
      }

      @keyframes shake-error {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
        20%, 40%, 60%, 80% { transform: translateX(2px); }
      }

      .celebration-header {
        transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
      }

      .celebration-header.updating {
        opacity: 0.7;
        transform: translateY(-5px);
      }
    `;
    document.head.appendChild(styles);
    log('🎨 Unified styles injected');
  };

  const createCelebrationHeader = () => {
    // Recalculate step number to ensure accuracy based on current DOM
    calculateStepNumber();

    const config = STATE_CONFIG[currentState] || STATE_CONFIG[UNIFIED_STATES.NEED_DESIGN];

    return `
      <div class="celebration-header">
        <div class="step-badge-container">
          <div class="step-overline">${config.headerOverline}</div>
          <h2 class="celebration-main-title">${config.headerTitle}</h2>
        </div>
        <div class="celebration-subtitle">
          ${config.headerSubtitle}
        </div>
      </div>
    `;
  };

  const injectCelebrationHeader = () => {
    if (!addToCartForm) return;

    celebrationHeader = addToCartForm.querySelector('.celebration-header');
    if (celebrationHeader) {
      log('✅ Celebration header already exists, updating...');
      updateCelebrationHeader();
      return;
    }

    const productFormButtons = addToCartForm.querySelector('.product-form__buttons');
    if (!productFormButtons) {
      log('❌ Cannot find product form buttons for header injection');
      return;
    }

    const headerElement = document.createElement('div');
    headerElement.innerHTML = createCelebrationHeader();
    celebrationHeader = headerElement.firstElementChild;

    try {
      productFormButtons.parentElement.insertBefore(celebrationHeader, productFormButtons);
      log('✅ Celebration header injected successfully');
    } catch (error) {
      log('⚠️ Header injection failed:', error);
      addToCartForm.appendChild(celebrationHeader);
    }
  };

  const setupStateManagerListeners = () => {
    stateManager.on('project:changed', handleProjectChanged);
    stateManager.on('project:cleared', handleProjectCleared);
    stateManager.on('project:restored', handleProjectRestored);
    stateManager.on('system:ready', handleSystemReady);
    stateManager.on('upload:completed', handleUploadCompleted);
    stateManager.on('upload:cleared', handleUploadCleared);
    stateManager.on('upload:mode:changed', handleUploadModeChanged);
    stateManager.on('design:method:changed', handleDesignMethodChanged);
    stateManager.on('variant:hardware-only:changed', handleHardwareOnlyChanged);
    log('🔗 StateManager listeners attached');

    // Listen for successful cart additions to clear upload state
    document.addEventListener('cart:item-added', handleCartItemAdded);
    document.addEventListener('cart:updated', handleCartUpdated);
    log('🛒 Cart event listeners attached');
  };

  const handleProjectChanged = async (event) => {
    const projectData = event.detail;
    log('📡 Project changed via StateManager:', projectData);

    clearValidationCache();

    if (projectData?.projectId) {
      setState(UNIFIED_STATES.READY_TO_ADD, false, 'Design saved! Ready to add to cart.');
      setProjectIdInForm(projectData.projectId);
    } else {
      await performInitialStateAnalysis();
    }
  };

  const handleProjectCleared = async () => {
    log('📡 Project cleared via StateManager');
    clearValidationCache();
    setState(UNIFIED_STATES.NEED_DESIGN, false, 'Project cleared. Please start designing again.');
  };

  const handleProjectRestored = async (event) => {
    const projectData = event.detail;
    log('📡 Project restored via StateManager:', projectData);
    setState(UNIFIED_STATES.READY_TO_ADD, false, 'Design restored! Ready to add to cart.');
  };

  const handleSystemReady = async () => {
    log('📡 System ready');
    await performInitialStateAnalysis();
  };

  const handleUploadCompleted = async (event) => {
    const uploadData = event.detail;
    log('📡 File upload completed via StateManager:', uploadData);

    clearValidationCache();

    if (uploadData) {
      // Check if user selected "Send Files Later"
      const uploadMode = uploadData?.mode || stateManager.getUploadMode();
      log('📧 Upload mode:', uploadMode);

      if (uploadMode === 'send_later') {
        setState(UNIFIED_STATES.SENDING_LATER, false, 'Files will be sent later.');
      } else {
        setState(UNIFIED_STATES.READY_TO_ADD, false, 'Files uploaded! Ready to add to cart.');
      }
      setFileUploadDataInForm(uploadData);
    } else {
      await performInitialStateAnalysis();
    }
  };

  const handleUploadCleared = async () => {
    log('📡 File upload cleared via StateManager');
    clearValidationCache();

    // Re-analyze state to determine correct state
    // (could be need_design for PitchPrint or need_file_upload for file upload)
    await performInitialStateAnalysis();
  };

  const handleUploadModeChanged = async (event) => {
    const mode = event.detail;
    log('📡 Upload mode changed via StateManager:', mode);

    clearValidationCache();

    if (mode === 'send_later') {
      // "Send later" mode means files will be sent via email
      setState(UNIFIED_STATES.SENDING_LATER, false, 'Files will be sent later.');
    }
    // Note: When switching to 'upload_now', don't run analysis here
    // The upload:cleared event will fire immediately after and handle state
  };

  const handleDesignMethodChanged = async (event) => {
    const method = event.detail;
    log('📡 Design method changed via StateManager:', method);

    // Clear ALL form properties to prevent conflicts
    clearAllDesignProperties();
    clearValidationCache();

    // Re-analyze state based on new method
    await performInitialStateAnalysis();

    log(`✅ Switched to ${method} design method, form cleared and state refreshed`);
  };

  /**
   * Handle hardware-only variant state changes
   * When a hardware-only variant is selected, switch to STANDARD_PRODUCT state
   * When switching back to a customizable variant, re-analyze state
   */
  const handleHardwareOnlyChanged = async (event) => {
    const { isHardwareOnly } = event.detail;
    log('📡 Hardware-only state changed:', isHardwareOnly);

    clearValidationCache();

    if (isHardwareOnly) {
      // Switch to standard product state - no customization required
      setState(UNIFIED_STATES.STANDARD_PRODUCT, false, 'Hardware-only variant selected');
      log('🔧 Switched to STANDARD_PRODUCT state for hardware-only variant');
    } else {
      // Switch back to customizable variant - re-analyze state
      log('🎨 Switched back to customizable variant - re-analyzing state');
      await performInitialStateAnalysis();
    }
  };

  const handleCartItemAdded = (event) => {
    log('🛒 Cart item added event, clearing design state');
    clearDesignState();
  };

  const handleCartUpdated = (event) => {
    log('🛒 Cart updated event, clearing design state');
    clearDesignState();
  };

  // Monitor cart count changes (more reliable than cart events)
  let lastCartCount = null;

  const setupCartCountMonitor = () => {
    // Get initial cart count
    const getCartCount = () => {
      const cartCountSelectors = [
        '[data-cart-count]',
        '.cart-count',
        '#cart-count',
        '.cart-icon-bubble',
        '[id*="cart-count"]',
        '[class*="cart-count"]'
      ];

      for (const selector of cartCountSelectors) {
        const element = document.querySelector(selector);
        if (element) {
          const count = parseInt(element.textContent || element.dataset.cartCount || '0');
          if (!isNaN(count)) {
            return count;
          }
        }
      }

      return null;
    };

    lastCartCount = getCartCount();
    log(`🛒 Initial cart count: ${lastCartCount}`);

    // Poll for cart count changes every 500ms
    setInterval(() => {
      const currentCount = getCartCount();

      if (currentCount !== null && lastCartCount !== null && currentCount > lastCartCount) {
        log(`🛒 Cart count increased: ${lastCartCount} → ${currentCount}`);
        clearDesignState();
      }

      lastCartCount = currentCount;
    }, 500);

    // Also watch for cart drawer/mini-cart opening (indicates successful add)
    const cartDrawerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const isCartDrawer = node.classList?.contains('cart-drawer') ||
                                node.id === 'cart-drawer' ||
                                node.classList?.contains('mini-cart') ||
                                node.getAttribute('aria-label')?.includes('cart');

            if (isCartDrawer) {
              log('🛒 Cart drawer opened (add-to-cart likely succeeded)');
              setTimeout(() => clearDesignState(), 500);
            }
          }
        });
      });
    });

    cartDrawerObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    log('✅ Cart count monitor and drawer observer initialized');
  };

  const clearDesignState = () => {
    // CRITICAL: Only clear the design method that was actually used
    // Check what's currently active and only clear that

    if (!stateManager) {
      log('⚠️ StateManager not available, cannot clear design state');
      return;
    }

    const uploadData = stateManager.getUploadData();
    const uploadMode = stateManager.getUploadMode();
    const projectId = stateManager.getProjectId();

    // Clear file upload data if it exists, BUT NOT if mode is "send_later"
    // "Send later" mode should persist after cart addition since no files were uploaded
    if (uploadData && uploadMode !== 'send_later') {
      log('🧹 Clearing file upload data from StateManager');
      stateManager.clearUploadData();

      // Clear hidden form inputs for file upload properties
      clearFileUploadProperties();

      // Clear file uploader UI
      if (window.FileUploader && typeof window.FileUploader.reset === 'function') {
        log('🧹 Resetting file uploader UI');
        window.FileUploader.reset();
      }
    } else if (uploadMode === 'send_later') {
      log('📧 Preserving "Send Files Later" state after cart addition');
    }

    // Clear PitchPrint project if it exists
    if (projectId) {
      log('🧹 Clearing PitchPrint project from StateManager');
      stateManager.clearProject();
    }

    // Reset form to initial state. Re-run the analysis instead of hardcoding
    // NEED_DESIGN — a non-customizable product (bundle / standard item) has no
    // design to start, so it must return to READY_TO_ADD, not "Ready to Design?".
    // PitchPrint / file-upload products correctly resolve back to NEED_DESIGN here.
    setTimeout(() => {
      performInitialStateAnalysis();
      log('✅ Design state cleared, ready for new order');
    }, 1000);
  };

  const setupPitchPrintIntegration = () => {
    let attempts = 0;
    const maxAttempts = 50;

    const setupHandler = () => {
      attempts++;

      if (window.ppclient) {
        if (window.ppclient.on) {
          window.ppclient.on('project-saved', handlePitchPrintProjectSaved);
          log('✅ PitchPrint project-saved handler attached');
        }

        clearInterval(handlerSetupInterval);
        return;
      }

      if (attempts % 10 === 0) {
        log(`⏳ Waiting for PitchPrint... (${attempts}/${maxAttempts})`);
      }

      if (attempts >= maxAttempts) {
        clearInterval(handlerSetupInterval);
        log('⚠️ PitchPrint timeout - using StateManager only');
      }
    };

    const handlerSetupInterval = setInterval(setupHandler, 200);
  };

  const handlePitchPrintProjectSaved = async (data) => {
    log('🎉 PitchPrint project saved:', data);

    // 2026-04-30: Auto-saves should not progress the ATC state machine. Without this guard,
    // every 5-minute auto-save moved state to READY_TO_ADD and wrote projectId to the form,
    // contributing to the cascade that crashed the iframe (see pitchprint-save-for-later-redirect.js).
    if (data?.data?.autoSaved || data?.autoSaved) {
      log('⏭️ Skipping autoSaved project-saved — ATC state machine reacts to manual saves only');
      return;
    }

    const projectId = data.data?.projectId || data.projectId;
    const numPages = data.data?.numPages || data.numPages;
    const shape = data.data?.shape || data.shape;

    if (projectId) {
      // Pass full project data to StateManager including metadata
      if (stateManager && stateManager.setProject) {
        stateManager.setProject({
          projectId,
          numPages,
          shape,
          variant: null // Variant data handled separately
        });
        log('✅ Project metadata saved to StateManager:', { projectId, numPages, shape });
      }

      setState(UNIFIED_STATES.READY_TO_ADD, false, 'Design saved! Ready to add to cart.');
      setProjectIdInForm(projectId);
    }
  };

    const showUnifiedToast = (type, message, duration = 3000) => {
    // Route all toasts through the centralized toast control system
    if (stateManager && window.PitchPrintToastControl && window.TOAST_CONFIG) {
      log('🔄 Routing toast through centralized control system:', { type, message });

      // Map unified states to toast control message types
      const messageTypeMap = {
        'ready_to_add': 'projectSaved',
        'need_design': 'projectCleared',
        'error': 'errorOccurred',
        'success': 'addToCartSuccess'
      };

      const centralMessageType = messageTypeMap[type] || 'manualTrigger';

      // Only show if enabled in toast control system
      if (window.TOAST_CONFIG.enabled && window.TOAST_CONFIG.messages[centralMessageType]) {
        log('✅ Toast allowed by centralized control system:', centralMessageType);

        // Use the correct StateManager event emission
        const eventData = {
          title: type === 'ready_to_add' ? 'Design Saved!' : 'PitchPrint',
          message: message,
          type: 'success',
          projectId: stateManager.getProjectId(),
          messageType: centralMessageType
        };

        // Trigger the toast through StateManager
        try {
          stateManager.dispatchEvent(new CustomEvent('toast:activate', { detail: eventData }));
          log('📡 Toast event dispatched successfully');
        } catch (error) {
          log('❌ Error dispatching toast event:', error);
          // Fallback: try direct toast activation if available
          if (window.ToastPreviewStateManager && window.ToastPreviewStateManager.activate) {
            window.ToastPreviewStateManager.activate(eventData);
          }
        }
      } else {
        log('🔇 Toast blocked by centralized control system:', centralMessageType, {
          enabled: window.TOAST_CONFIG.enabled,
          messageEnabled: window.TOAST_CONFIG.messages[centralMessageType]
        });
      }
    } else {
      log('🔇 Centralized toast control not available, toast suppressed');
    }
  };

  // =====================================
  // PUBLIC API
  // =====================================

  window.UnifiedAddToCartSystem = {
    getState: () => ({
      currentState,
      stepNumber,
      hasProject: stateManager.getProjectId(),
      isPitchPrintProduct: isPitchPrintProduct(),
      performanceMetrics
    }),

    setState,
    forceStateAnalysis: performInitialStateAnalysis,
    clearCache: clearValidationCache,

    debugPitchPrintData: () => {
      const projectId = getProjectId();
      log('🔍 Current PitchPrint data state:');
      console.log('Project ID:', projectId);

      if (addToCartForm) {
        const pitchPrintInputs = addToCartForm.querySelectorAll(`
          input[name*="pitchprint"],
          input[name*="_pitchprint"],
          input[name*="pprint"]
        `);

        console.log('Form inputs:');
        pitchPrintInputs.forEach(input => {
          console.log(`  ${input.name}: ${input.value}`);
        });
      }
    },

    verifyFormData: () => {
      if (!addToCartForm) {
        console.log('No form found');
        return;
      }

      try {
        const formData = new FormData(addToCartForm);
        console.log('FormData test successful');

        for (let [key, value] of formData.entries()) {
          if (key.includes('properties')) {
            console.log(`${key}: ${value}`);
          }
        }
      } catch (error) {
        console.error('FormData test failed:', error);
      }
    }
  };

  // =====================================
  // INITIALIZATION
  // =====================================

  window.UNIFIED_ADD_TO_CART_ACTIVE = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }

  log('🚀 Unified Add-to-Cart System loaded and ready for initialization');
})();
